import React, { useState } from 'react';
import { Button, Card, Modal, Space, Table, TablePaginationConfig, Input, Popover, Select, Tabs } from 'antd';
import { DeleteOutlined, ExclamationCircleOutlined, FormOutlined } from '@ant-design/icons';
import {
  Rule,
  RulesWithDeviceType,
  DeviceType,
  SuperGroup,
  StbGroupExtended,
  DeviceTypeSettings,
} from '@services/types';

import api from '@services/api';
import { toast } from 'react-toastify';
import { useParams, useHistory } from 'react-router-dom';
import styles from '@layouts/firmwares/Rules/rules.module.scss';
import { selectSearchFilter } from '@services/utils';
import { AppContext } from '@layouts/AppRouter';
import { ApiUserColumnsResponse } from '@openapi';
import UnusedRule from '@layouts/firmwares/UnusedRules';
import { Navigation } from '@constants/Navigation';

const { Column } = Table;
const { Search } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const Rules: React.FC = () => {
  const { user, columnsRules, setColumnsRules, columnsRulesEtc, setColumnsRulesEtc, deviceFilter, setDeviceFilter } =
    React.useContext(AppContext);
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [isShowCustomFirmware1Version, setIsShowCustomFirmware1Version] = useState(true);
  const [isShowCustomFirmware2Version, setIsShowCustomFirmware2Version] = useState(true);
  const [isShowCustomFirmware3Version, setIsShowCustomFirmware3Version] = useState(true);
  const [rawRules, setRawRules] = useState<Rule[]>([]);
  const [rules, setRules] = useState<RulesWithDeviceType[]>([]);
  const [deviceTypes, setDeviceTypes] = useState<DeviceType[]>([]);
  const [deviceSetting, setDeviceSetting] = useState<DeviceTypeSettings>();
  const [selectedDeviceType, setSelectedDeviceType] = useState<DeviceType['id']>();
  const [selectedSuper, setSelectedSuper] = useState<SuperGroup['id']>();
  const [superGroups, setSuperGroups] = useState<SuperGroup[]>([]);
  const [searchResult, setSearchResult] = useState('');
  const [pagination, setPagination] = React.useState<TablePaginationConfig>({
    current: 1,
    pageSize: 10,
    showSizeChanger: rules.length > 10,
    total: rules.length,
  });

  React.useEffect(() => {
    setRules(
      rawRules.map((rule) => ({
        ...rule,
        deviceTypeName: deviceTypes.find((deviceType) => deviceType.id === rule.deviceTypeId)?.deviceName,
        superGroupName: superGroups.find((superGroup) => superGroup.id === rule.deviceTypeId)?.name,
      })),
    );
    setIsShowCustomFirmware1Version(
      rawRules.some((rule) => rule.customFirmware1VersionId && deviceSetting?.fwExtraName1?.length),
    );
    setIsShowCustomFirmware2Version(
      rawRules.some((rule) => rule.customFirmware2VersionId && deviceSetting?.fwExtraName2?.length),
    );
    setIsShowCustomFirmware3Version(
      rawRules.some((rule) => rule.customFirmware3VersionId && deviceSetting?.fwExtraName3?.length),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceTypes, rawRules, superGroups, deviceSetting]);

  const handleSearch = (newPagination = pagination) => {
    if (deviceFilter?.length) {
      api.rules
        .getRules({
          filterDeviceTypeIds: deviceFilter ? [deviceFilter] : [''],
          limit: newPagination.pageSize,
          offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
        })
        .then((data) => {
          setRawRules(data.elements);
          setPagination({
            current: newPagination.current,
            pageSize: newPagination.pageSize,
            showSizeChanger: true,
            total: data.totalElementCount || data.elements.length,
          });
          setDeviceFilter('');
        });
    } else {
      if (searchResult.length >= 0) {
        api.rules
          .getRules({
            filterDeviceTypeIds: selectedDeviceType ? [selectedDeviceType] : [''],
            filterSuperGroupId: selectedSuper,
            filter: searchResult,
            limit: newPagination.pageSize,
            offset: (newPagination.current || 0) * (newPagination.pageSize || 0) - (newPagination.pageSize || 0),
          })
          .then((data) => {
            setRawRules(data.elements);
            setPagination({
              current: newPagination.current,
              pageSize: newPagination.pageSize,
              showSizeChanger: true,
              total: data.totalElementCount || data.elements.length,
            });
          });
      }
    }
  };

  React.useEffect(() => {
    handleSearch();
    if (selectedDeviceType) {
      api.administration.getDeviceTypeSettings(selectedDeviceType).then((item) => {
        setDeviceSetting(item);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceType, selectedSuper, searchResult.length]);

  React.useEffect(() => {
    if (user?.id) {
      Promise.all([
        api.administration.getDeviceTypes(),
        api.administration.getSuperGroups(),
        api.administration.getColumnsRules(user.id),
      ])
        .then(([deviceType, superGroup, rulesColumn]) => {
          if (deviceType && superGroup) {
            setDeviceTypes(deviceType);
            setSuperGroups(superGroup);
            setColumnsRules(rulesColumn.columns);
            setColumnsRulesEtc(rulesColumn.etc);

            setLoading(false);
          }
        })
        .catch((error) => {
          console.error(error);
          toast.error('Firmwares was not loaded due to error');
        });
    }
  }, []);

  const handleTableChange = React.useCallback(
    (newPagination: TablePaginationConfig) => {
      setPagination(newPagination);
      handleSearch(newPagination);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedDeviceType, selectedSuper],
  );

  const showDeleteConfirm = (rule: Rule) => {
    Modal.confirm({
      title: 'Are you sure delete this rule?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await api.rules.deleteRule(rule.id);
          setRules(rules.filter(({ id }) => id !== rule.id));
          toast.success('Rule was deleted successfully');
        } catch (error) {
          toast.error('Rule was not deleted');
        }
      },
    });
  };

  const ruleCount = (pagination.pageSize || 0) * (pagination.current || 0);
  const ruleItem = (ruleCount || 0) - ((pagination.pageSize || 0) - 1);

  const handleChange = (value: string[]) => {
    setColumnsRules(value);
    const newItems = [...(columnsRules || []), ...(columnsRulesEtc || [])].filter((item) => !value.includes(item));
    setColumnsRulesEtc(newItems);
  };

  React.useEffect(() => {
    if (user?.id && columnsRules) {
      api.administration.updateColumnsRules(user.id, { columns: columnsRules });
    }
  }, [columnsRules]);
  return (
    <Tabs type="card" size="large" defaultActiveKey={'rules' || 'unused'}>
      <TabPane tab="Rules" key="rules">
        <Card
          title={`Upgrade Rules: ${ruleItem}-${ruleCount} / ${pagination.total}`}
          extra={
            <Space className={styles.item}>
              <div className={styles.filter}>
                <Select
                  className={styles.selectDevice}
                  allowClear
                  optionFilterProp="deviceTypes"
                  placeholder="Select a Device Type"
                  onChange={(e: DeviceType['id']) => {
                    setSelectedDeviceType(e);
                    setSelectedSuper(undefined);
                    setPagination({
                      ...pagination,
                      current: 1,
                    });
                  }}
                  value={selectedDeviceType}
                  showSearch
                  filterOption={selectSearchFilter}
                >
                  {deviceTypes?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.deviceName}
                    </Option>
                  ))}
                </Select>
                <Select
                  className={styles.selectDevice}
                  placeholder="Select SuperGroup"
                  onChange={(e: SuperGroup['id']) => {
                    setSelectedSuper(e);
                    setPagination({
                      ...pagination,
                      current: 1,
                    });
                  }}
                  allowClear
                  showSearch
                  value={selectedSuper}
                  filterOption={selectSearchFilter}
                >
                  {superGroups?.map((item) => (
                    <Option key={item.id} value={item.id}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
                <Search
                  onChange={(e) => {
                    setSearchResult(e.target.value);
                  }}
                  placeholder="Search"
                  allowClear
                  enterButton
                  size="middle"
                  onSearch={() => handleSearch()}
                />
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '300px max-content',
                  gridGap: '10px',
                  alignItems: 'center',
                }}
              >
                <Select
                  mode="multiple"
                  allowClear
                  style={{ width: '100%', minWidth: '300px', maxWidth: '700px' }}
                  placeholder="Please select columns"
                  filterOption={selectSearchFilter}
                  value={columnsRules}
                  onChange={handleChange}
                  maxTagCount={2}
                >
                  {columnsRules?.map((item) => (
                    <Select.Option key={item}>{item}</Select.Option>
                  ))}{' '}
                  {columnsRulesEtc?.map((item) => (
                    <Select.Option key={item}>{item}</Select.Option>
                  ))}
                </Select>
                <Button type="primary" onClick={() => history.push(Navigation.CreateRule)}>
                  Add
                </Button>
              </div>
            </Space>
          }
          bodyStyle={{ padding: 0 }}
        >
          <Table
            loading={loading}
            dataSource={rules}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey={(record: RulesWithDeviceType) => record.id}
            scroll={{ x: 1200 }}
          >
            {columnsRules?.includes('Device Type') && (
              <Column
                align="center"
                title="Device Type"
                dataIndex="deviceTypeName"
                render={(text, record: RulesWithDeviceType) => record?.etc?.deviceTypeName}
              />
            )}
            {columnsRules?.includes('Name') && (
              <Column
                align="center"
                title="Name"
                dataIndex="name"
                key="Name"
                render={(text, record: RulesWithDeviceType) => record?.name}
              />
            )}
            {columnsRules?.includes('Ui Version') && (
              <Column
                align="center"
                title="Ui Version"
                dataIndex="etc.uiVersion"
                render={(text, record: RulesWithDeviceType) => record?.etc?.uiVersion}
              />
            )}
            {columnsRules?.includes('FW Version') && (
              <Column
                align="center"
                title="FW Version"
                dataIndex="etc.firmwareVersion"
                render={(text, record: RulesWithDeviceType) => record.etc?.firmwareVersion}
              />
            )}
            {columnsRules?.includes('Loader Version') && (
              <Column
                align="center"
                title="Loader Version"
                dataIndex="etc.loaderVersion"
                render={(text, record: RulesWithDeviceType) => record.etc?.loaderVersion}
              />
            )}
            {columnsRules?.includes('Super group') && (
              <Column
                align="center"
                title="Scope"
                dataIndex="superGroupName"
                ellipsis
                render={(text, record: RulesWithDeviceType) => record.etc?.superGroupName}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware1Version && columnsRules?.includes('CFW1') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName1}
                dataIndex="etc.customFirmware1Version"
                render={(text, record: RulesWithDeviceType) => record?.etc?.customFirmware1Version}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware2Version && columnsRules?.includes('CFW2') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName2}
                dataIndex="etc.customFirmware2Version"
                render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware2Version}
              />
            )}
            {selectedDeviceType && isShowCustomFirmware3Version && columnsRules?.includes('CFW3') && (
              <Column
                align="center"
                title={deviceSetting?.fwExtraName3}
                dataIndex="etc.customFirmware3Version"
                render={(text, record: RulesWithDeviceType) => record.etc?.customFirmware3Version}
              />
            )}
            {columnsRules?.includes('Attributes') && (
              <Column
                align="center"
                title="Attributes"
                dataIndex="attributes"
                key="attributes"
                render={(text, record: RulesWithDeviceType) =>
                  record.forceUpdate ? (
                    record.sendAllFirmwares && record?.deviceTypeSendAllFirmwares ? (
                      <>
                        <Popover content="Forced rule (Allow downgrade)">
                          <Button
                            style={{
                              fontWeight: 'bold',
                              fontSize: '13px',
                              padding: '2px 5px',
                              width: '2em',
                              height: '2em',
                              borderRadius: '50%',
                            }}
                            type="primary"
                          >
                            F
                          </Button>
                        </Popover>
                        <Popover content="Allow send all firmwares at once to devices.">
                          <Button
                            style={{
                              fontWeight: 'bold',
                              fontSize: '13px',
                              padding: '2px 5px',
                              width: '2em',
                              height: '2em',
                              borderRadius: '50%',
                              marginLeft: '5px',
                            }}
                            type="primary"
                          >
                            A
                          </Button>
                        </Popover>
                      </>
                    ) : (
                      <Popover content="Forced rule (Allow downgrade)">
                        <Button
                          style={{
                            fontWeight: 'bold',
                            fontSize: '13px',
                            padding: '2px 5px',
                            width: '2em',
                            height: '2em',
                            borderRadius: '50%',
                          }}
                          type="primary"
                        >
                          F
                        </Button>
                      </Popover>
                    )
                  ) : record.sendAllFirmwares && record?.deviceTypeSendAllFirmwares ? (
                    <Popover content="Allow send all firmwares at once to devices.">
                      <Button
                        style={{
                          fontWeight: 'bold',
                          fontSize: '13px',
                          padding: '2px 5px',
                          width: '2em',
                          height: '2em',
                          borderRadius: '50%',
                          marginLeft: '5px',
                        }}
                        type="primary"
                      >
                        A
                      </Button>
                    </Popover>
                  ) : null
                }
              />
            )}
            <Column
              align="center"
              width="100px"
              fixed="right"
              title="Action"
              key="action"
              render={(text, newRules: RulesWithDeviceType) =>
                newRules.stbGroup && newRules.stbGroup?.length >= 1 ? (
                  <FormOutlined onClick={() => history.push(Navigation.UpdateRule.replace(':id', newRules.id))} />
                ) : (
                  <Space size="middle">
                    <FormOutlined onClick={() => history.push(Navigation.UpdateRule.replace(':id', newRules.id))} />
                    <DeleteOutlined onClick={() => showDeleteConfirm(newRules)} />
                  </Space>
                )
              }
            />
          </Table>
        </Card>
      </TabPane>
      <TabPane tab="Unused Rules" key="unused">
        <UnusedRule />
      </TabPane>
    </Tabs>
  );
};
export default React.memo(Rules);
